import { useEffect, useRef } from 'react';

interface Options {
  enabled: boolean;
  onShake: () => void;
  cooldownMs?: number;
}

// Detect a *deliberate* hard shake via DeviceMotion. Tuned deliberately strict:
// a car ride is bumpy, so a single jolt must NOT fire. We require several strong
// jerk spikes within a short window, plus a long cooldown, so only an intentional
// vigorous shake triggers the emergency alert. iOS 13+ requires motion permission
// to be granted first (requested from a user gesture in the Emergency screen).
export function useShakeDetection({ enabled, onShake, cooldownMs = 30000 }: Options) {
  const lastRef = useRef({ x: 0, y: 0, z: 0, t: 0 });
  const spikesRef = useRef<number[]>([]);
  const lastFireRef = useRef(0);
  const onShakeRef = useRef(onShake);
  onShakeRef.current = onShake;

  useEffect(() => {
    if (!enabled) return;

    const THRESHOLD = 22;  // m/s^2 of instantaneous jerk to count as one spike
    const NEEDED = 3;      // spikes required within WINDOW to qualify as a shake
    const WINDOW = 1200;   // ms - the spikes must cluster this tightly

    const handler = (e: DeviceMotionEvent) => {
      const acc = e.accelerationIncludingGravity;
      if (!acc || acc.x == null) return;
      const now = Date.now();
      const last = lastRef.current;
      if (now - last.t < 60) return; // cap sampling ~16Hz
      const delta =
        Math.abs((acc.x || 0) - last.x) +
        Math.abs((acc.y || 0) - last.y) +
        Math.abs((acc.z || 0) - last.z);
      const firstReading = last.t === 0;
      lastRef.current = { x: acc.x || 0, y: acc.y || 0, z: acc.z || 0, t: now };
      if (firstReading) return;

      if (delta > THRESHOLD) {
        const spikes = spikesRef.current.filter((ts) => now - ts <= WINDOW);
        spikes.push(now);
        spikesRef.current = spikes;
        if (spikes.length >= NEEDED && now - lastFireRef.current > cooldownMs) {
          lastFireRef.current = now;
          spikesRef.current = [];
          onShakeRef.current();
        }
      }
    };

    window.addEventListener('devicemotion', handler);
    return () => window.removeEventListener('devicemotion', handler);
  }, [enabled, cooldownMs]);
}
